import React, { useState, useEffect, useCallback } from 'react';
import { User, LogOut, Shield, Key, CheckCircle, AlertCircle } from 'lucide-react';
import axios from 'axios';

const CONFIG = {
  API_BASE_URL: process.env.REACT_APP_API_BASE_URL || 'https://core.workpay.test/api',
  CLIENT_ID: process.env.REACT_APP_CLIENT_ID || '9efa9fe1-1034-46d8-b181-e032bfe2cacb',
  REDIRECT_URI: process.env.REACT_APP_REDIRECT_URI || 'http://localhost:3000/',
  ORIGIN_URL: process.env.REACT_APP_ORIGIN_URL || 'http://localhost:3000/'
};

// Auth service
const authService = {
  getStoredToken: () => {
    return sessionStorage.getItem('oauth_token');
  },

  getStoredAccessToken: () => {
    return sessionStorage.getItem('access_token');
  },

  getStoredRefreshToken: () => {
    return sessionStorage.getItem('refresh_token');
  },

  setUserInformation: (user_info) => {
    sessionStorage.setItem('user_info', user_info);
  },

  setToken: (token) => {
    sessionStorage.setItem('oauth_token', token);
  },

  setAccessToken: (token) => {
    sessionStorage.setItem('access_token', token);
  },

  setRefreshToken: (token) => {
    sessionStorage.setItem('refresh_token', token);
  },

  removeToken: () => {
    sessionStorage.removeItem('oauth_token');
  },

  isAuthenticated: () => {
    return authService.getStoredToken(); //check if expired
  },

  getUserInfo: () => {
    return sessionStorage.getItem('user_info');
  },

  initiateOAuth: () => {
    const params = new URLSearchParams({
      client_id: CONFIG.CLIENT_ID,
      response_type: 'code',
      state: CONFIG.ORIGIN_URL,
      redirect_uri: CONFIG.REDIRECT_URI
    });

    window.location.href = `${CONFIG.API_BASE_URL}/oauth/login?${params.toString()}`;
  },

  logout: () => {
    authService.removeToken();
    window.location.reload();
  },

  logoutRedirect: () => {
    authService.removeToken();
    window.location.href = `${CONFIG.ORIGIN_URL}`;
  }
};

// Main App Component
const OAuthApp = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [flowStep, setFlowStep] = useState('checking');
  const [logs, setLogs] = useState([]);

  // Function to add logs to the state
  const addLog = useCallback((type, message, data = null) => {
    const timestamp = new Date().toISOString();
    const logEntry = {
      id: Date.now(),
      timestamp,
      type,
      message,
      data
    };

    console[type](message, data);
    setLogs(prevLogs => [...prevLogs, logEntry]);
  }, []);

  // Function to clear logs
  const clearLogs = useCallback(() => {
    setLogs([]);
    addLog('log', 'Logs cleared');
  }, [addLog]);


  const handleAuthCallback = useCallback(async (code, state) => {
    try {
      setFlowStep('processing');
      addLog('log', 'OAuth Callback Received:', { code, state });

      const tokenUrl = `${CONFIG.API_BASE_URL}/oauth/token/code-grant`;
      const profileUrl = `${CONFIG.API_BASE_URL}/v2/user/auth-details`;

      const commonHeaders = {
        'Content-Type': 'application/json',
        'Accept': 'application/json',
      };

      // Step 1: Exchange code for tokens
      const tokenResponse = await axios.post(tokenUrl, {
        code,
        state,
        client_id: CONFIG.CLIENT_ID,
      }, {
        withCredentials: true,
        headers: commonHeaders,
        timeout: 10000,
      });

      addLog('log', 'Tokens received:', tokenResponse.data);

      if (!tokenResponse.data.success) {
        throw new Error('Token exchange failed');
      }

      const tokenData = tokenResponse.data.data;
      authService.setToken(tokenData);
      authService.setAccessToken(tokenData.access_token);
      authService.setRefreshToken(tokenData.refresh_token);

      // Step 2: Get user profile
      const authProfileResponse = await axios.get(profileUrl, {
        headers: {
          'Authorization': `Bearer ${tokenData.access_token}`,
          ...commonHeaders,
        },
      });

      addLog('log', 'Auth user details', authProfileResponse.data);

      if (!authProfileResponse.data.success) {
        throw new Error('Failed to fetch user profile');
      }

      const userData = authProfileResponse.data.data;
      const authDetails = {
        sub: userData.username,
        name: userData.name,
        email: userData.email,
        iat: Math.floor(Date.now() / 1000),
        exp: Math.floor(Date.now() / 1000) + (60 * 60), // 1 hour
        access_token: tokenData.access_token,
        refresh_token: tokenData.refresh_token,
      };

      authService.setUserInformation(JSON.stringify(authDetails));
      setUser(authDetails);
      setFlowStep('authenticated');

      // Clean up URL
      window.history.replaceState({}, document.title, window.location.pathname);

    } catch (err) {
      addLog('error', 'Error during OAuth callback:', err);
      setError('Failed to exchange authorization code for token');
      setFlowStep('error');
    } finally {
      setLoading(false);
    }


  }, [setFlowStep, setUser, setError, setLoading, addLog]);

  const handleAuthFlow = useCallback(async () => {
    try {
      setLoading(true);
      setFlowStep('checking');

      // Check if we have an authorization code in the URL (returning from OAuth)
      const urlParams = new URLSearchParams(window.location.search);
      const authCode = urlParams.get('code');
      const state = urlParams.get('state');

      // Log URL parameters for debugging
      addLog('log', 'URL Parameters:', Object.fromEntries(urlParams.entries()));

      if (authCode) {
        addLog('log', 'Authorization code detected, proceeding with token exchange');
        setFlowStep('exchanging');
        await handleAuthCallback(authCode, state);
        return;
      }

      // Check if we already have a valid token
      if (authService.isAuthenticated()) {
        addLog('log', 'User is already authenticated, using stored token');
        setFlowStep('authenticated');
        const userInfo = JSON.parse(authService.getUserInfo() || '{}');
        addLog('log', 'User info from stored token:', userInfo);
        setUser(userInfo);
        setLoading(false);
        return;
      }

      // No token and no auth code - need to authenticate
      addLog('log', 'No authentication found, user needs to log in');
      setFlowStep('unauthenticated');
      setLoading(false);

    } catch (err) {
      addLog('error', 'Error during auth flow:', err);
      setError(err.message);
      setLoading(false);
    }
  }, [handleAuthCallback, setLoading, setFlowStep, setUser, setError, addLog]);

  useEffect(() => {
    handleAuthFlow();
  }, [handleAuthFlow]);

  const FlowStep = ({ step, title, description, icon: Icon, active = false }) => (
      <div className={`flex items-center p-4 rounded-lg border-2 transition-all ${
          active ? 'border-blue-500 bg-blue-50' : 'border-gray-200 bg-gray-50'
      }`}>
        <Icon className={`w-6 h-6 mr-3 ${active ? 'text-blue-600' : 'text-gray-400'}`} />
        <div>
          <h3 className={`font-semibold ${active ? 'text-blue-900' : 'text-gray-600'}`}>
            {title}
          </h3>
          <p className={`text-sm ${active ? 'text-blue-700' : 'text-gray-500'}`}>
            {description}
          </p>
        </div>
      </div>
  );

  // Log viewer component
  const LogViewer = ({ logs, onClear }) => {
    if (!logs || logs.length === 0) {
      return (
        <div className="bg-gray-50 rounded-lg p-6 text-center text-gray-500">
          No logs available
        </div>
      );
    }

    return (
      <div className="bg-gray-900 rounded-lg overflow-hidden">
        <div className="flex justify-between items-center p-2 bg-gray-800 border-b border-gray-700">
          <span className="text-gray-300 font-semibold">Console Output</span>
          {onClear && (
            <button 
              onClick={onClear}
              className="text-xs bg-gray-700 hover:bg-gray-600 text-white px-2 py-1 rounded transition-colors"
            >
              Clear Logs
            </button>
          )}
        </div>
        <div className="p-4 text-white font-mono text-sm overflow-auto max-h-96">
          {logs.map(log => (
            <div key={log.id} className={`mb-2 ${
              log.type === 'error' ? 'text-red-400' : 
              log.type === 'warn' ? 'text-yellow-400' : 'text-green-400'
            }`}>
              <span className="text-gray-400">[{new Date(log.timestamp).toLocaleTimeString()}]</span>{' '}
              <span className="font-semibold">{log.message}</span>
              {log.data && (
                <pre className="ml-6 mt-1 p-2 bg-gray-800 rounded overflow-x-auto">
                  {typeof log.data === 'object' 
                    ? JSON.stringify(log.data, null, 2) 
                    : String(log.data)}
                </pre>
              )}
            </div>
          ))}
        </div>
      </div>
    );
  };

  if (loading) {
    return (
        <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
          <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Processing...</h2>
              <p className="text-gray-600">
                {flowStep === 'checking' && 'Checking authentication status'}
                {flowStep === 'exchanging' && 'Exchanging authorization code'}
                {flowStep === 'processing' && 'Processing tokens'}
              </p>
            </div>
          </div>
        </div>
    );
  }

  if (error) {
    return (
        <div className="min-h-screen bg-gradient-to-br from-red-50 to-pink-100 flex items-center justify-center">
          <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full">
            <div className="text-center">
              <AlertCircle className="w-12 h-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-xl font-semibold text-gray-800 mb-2">Authentication Error</h2>
              <p className="text-red-600 mb-4">{error}</p>
              <button
                  onClick={() => window.location.reload()}
                  className="bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
              >
                Try Again
              </button>

              <button
                  onClick={authService.logoutRedirect}
                  className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors"
              >
                Clear Tokens
              </button>

              {/* Log Viewer for error view */}
              <div className="mt-8">
                <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                  </svg>
                  Debug Logs
                </h3>
                <LogViewer logs={logs} onClear={clearLogs} />
              </div>
            </div>
          </div>
        </div>
    );
  }

  if (user) {
    return (
        <div className="min-h-screen bg-gradient-to-br from-green-50 to-emerald-100">
          <div className="container mx-auto px-4 py-8">
            <div className="bg-white rounded-xl shadow-lg p-8 max-w-4xl mx-auto">
              <div className="flex items-center justify-between mb-8">
                <div className="flex items-center">
                  <CheckCircle className="w-8 h-8 text-green-600 mr-3" />
                  <h1 className="text-2xl font-bold text-gray-800">Authentication Successful</h1>
                </div>
                <button
                    onClick={authService.logout}
                    className="flex items-center bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
                >
                  <LogOut className="w-4 h-4 mr-2" />
                  Logout
                </button>
              </div>

              {/* User Info */}
              <div className="bg-gray-50 rounded-lg p-6 mb-8">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <User className="w-5 h-5 mr-2" />
                  User Information
                </h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium text-gray-600">Name</label>
                    <p className="text-gray-800">{user.name || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Email</label>
                    <p className="text-gray-800">{user.email || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">User ID</label>
                    <p className="text-gray-800 font-mono text-sm">{user.sub || 'N/A'}</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium text-gray-600">Token Expires</label>
                    <p className="text-gray-800">
                      {user.exp ? new Date(user.exp * 1000).toLocaleString() : 'N/A'}
                    </p>
                  </div>
                </div>
              </div>

              {/* OAuth Flow Steps */}
              <div>
                <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <Shield className="w-5 h-5 mr-2" />
                  OAuth Flow Completed
                </h2>
                <div className="space-y-3">
                  <FlowStep
                      step="1"
                      title="User Detection"
                      description="Detected no session, initiated OAuth flow"
                      icon={User}
                      active={true}
                  />
                  <FlowStep
                      step="2"
                      title="API Validation"
                      description="API backend validated client credentials and generated PKCE challenge"
                      icon={Shield}
                      active={true}
                  />
                  <FlowStep
                      step="3"
                      title="User Authentication"
                      description="User successfully authenticated with OAuth server"
                      icon={Key}
                      active={true}
                  />
                  <FlowStep
                      step="4"
                      title="Token Exchange"
                      description="Authorization code exchanged for access tokens via PKCE"
                      icon={CheckCircle}
                      active={true}
                  />
                </div>
              </div>

              {/* Log Viewer */}
              <div className="mt-8">
                <h2 className="text-lg font-semibold text-gray-800 mb-4 flex items-center">
                  <svg xmlns="http://www.w3.org/2000/svg" className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
                  </svg>
                  Development Logs
                </h2>
                <LogViewer logs={logs} onClear={clearLogs} />
              </div>
            </div>
          </div>
        </div>
    );
  }

  return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
        <div className="bg-white p-8 rounded-xl shadow-lg max-w-md w-full">
          <div className="text-center mb-8">
            <Shield className="w-16 h-16 text-blue-600 mx-auto mb-4" />
            <h1 className="text-2xl font-bold text-gray-800 mb-2">OAuth Authentication</h1>
            <p className="text-gray-600">Secure login with PKCE flow</p>
          </div>

          <div className="space-y-4 mb-8">
            <div className="bg-gray-50 p-4 rounded-lg">
              <h3 className="font-semibold text-gray-800 mb-2">Flow Details</h3>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• Stateless JWT-based authentication</li>
                <li>• PKCE (Proof Key for Code Exchange)</li>
                <li>• Secure token storage</li>
                <li>• Automatic session management</li>
              </ul>
            </div>
          </div>

          <button
              onClick={authService.initiateOAuth}
              className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg hover:bg-blue-700 transition-colors font-semibold"
          >
            Sign In with OAuth
          </button>

          <div className="mt-6 text-center">
            <p className="text-xs text-gray-500">
              Client ID: {CONFIG.CLIENT_ID.substring(0, 8)}...
            </p>
          </div>

          {/* Log Viewer for unauthenticated view */}
          <div className="mt-8">
            <h3 className="font-semibold text-gray-800 mb-2 flex items-center">
              <svg xmlns="http://www.w3.org/2000/svg" className="w-4 h-4 mr-2" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"></path>
              </svg>
              Development Logs
            </h3>
            <LogViewer logs={logs} onClear={clearLogs} />
          </div>
        </div>
      </div>
  );
};

export default OAuthApp;
